import { padNumber } from '../util';
import { StreakMilestone } from '../api/API';
import { computed, extendObservable, makeObservable, observable } from 'mobx';
import React from 'react';

export interface DebtInfo {
  invoiceDate: string;
  invoiceAmount: number;
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthMonth?: number;
  birthDay?: number;
  birthYear?: number;
  gender?: string;
  avatarUrl?: string;
  latestTrainingPackage?: TrainingPackage;
  pendingEarlyBirdPackage?: TrainingPackage;
  hasActiveMembership?: boolean;
  debtInfo?: DebtInfo;
  bookingHistory: Registration[];
  referralToken?: string;
}

export interface TrainingPackage {
  id: number;
  category: string;
  isActive: boolean;
  endDate: Date;
  packageName: string;
  pricePerCoin: number;
  packageStatus: string;
  availableCoins: number;
  isSinglePackage: boolean;
  isEarlyBird?: boolean;
  activateFromDate?: string;
  activateUntilDate?: string;
  canActivateEarlyBird?: boolean;
  trainingPeriodsAttended: TrainingPeriodBooking[]
}

export interface TrainingPeriodBooking {
  id: number;
  timestamp: Date;
}

export interface Registration {
  time: Date;
  cost: number;
  description: string;
  credit?: boolean;
  bookingType?: 'classes' | 'events' | string;
  singlePackage?: boolean;
  packagePrice?: number;
}

export interface ProgramTime {
  startTime: string;
  endTime: string;
  programName: string;
}

export interface PurchasedTickets {
  description: string;
  paymentMethod: string;
  eventId: number;
  eventTicketId: number;
  price: number;
  comment: string;
}

export interface Event {
  eventId: number;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  venueName: string;
  venueId: number;
  venueCity: string;
  venueStreet: string;
  styles: string;
  isInternational: boolean;
  link: string;
  facebookEventUrl: string;
  programTimes: ProgramTime[];
  tickets: EventTicket[];
  purchasedTickets: PurchasedTickets[];
  recommendedForUser?: boolean;
}
const PaymentMethod = {
  DANCE_COINS: 'dance_coins',
}
export class SellingItemCategory {
  @observable public id: number;
  @observable public priority: number;
  @observable public name: string;
  @observable public items: SellingItem[];
  @observable public tickets: EventTicket[];
  @observable public events: Event[];

  constructor({ priority, name, items, tickets, events }) {
    makeObservable(this);
    this.priority = priority;
    this.name = name;
    this.items = items ? items.map(item => new SellingItem(item)) : [];
    this.tickets = tickets ? tickets.map(ticket => new EventTicket(ticket)) : [];
    this.events = events || [];
    
    // If we have events, process their tickets and keep them nested under events
    if (this.events && this.events.length > 0) {
      // Clear the separate tickets array since tickets will be under events
      this.tickets = [];
      
      this.events.forEach(event => {
        const rawEventTickets = (event as any).eventTickets;
        if (rawEventTickets && rawEventTickets.length > 0) {
          // Convert raw eventTickets to EventTicket objects and assign to event.tickets
          event.tickets = rawEventTickets.map(eventTicket => new EventTicket({
            eventId: eventTicket.eventId,
            ticketTypeId: eventTicket.ticketTypeId,
            trainingId: eventTicket.trainingId,
            trainingPeriodId: eventTicket.trainingPeriodId,
            ticketName: eventTicket.ticketName,
            coinsPrice: eventTicket.coinsPrice,
            promoCoinsPrice: eventTicket.promoCoinsPrice,
            promoEndTime: eventTicket.promoEndTime,
            bookingPrice: eventTicket.promoCoinsPrice ?? eventTicket.coinsPrice,
            isPurchased: eventTicket.isPurchased
          }));
          
          // Remove the old eventTickets array since we now have tickets
          delete (event as any).eventTickets;
        } else {
          event.tickets = [];
        }

        // Process purchased tickets - keep them separate from event tickets
        const rawPurchasedTickets = (event as any).purchasedTickets;
        if (rawPurchasedTickets && rawPurchasedTickets.length > 0) {
          event.purchasedTickets = rawPurchasedTickets.map(purchasedTicket => ({
            description: purchasedTicket.comment || purchasedTicket.description,
            paymentMethod: purchasedTicket.paymentMethod,
            eventId: purchasedTicket.eventId,
            eventTicketId: purchasedTicket.eventTicketId,
            price: purchasedTicket.price,
            comment: purchasedTicket.comment || ''
          }));
        } else {
          event.purchasedTickets = [];
        }
      });
    }
  }
}


export class EventTicket {
  public eventId: number;
  public ticketTypeId: number;
  public trainingId: number;
  public trainingPeriodId: number;
  public ticketName: string;
  public coinsPrice: number;
  public promoCoinsPrice: number;
  public promoEndTime: string | null;
  public selected: boolean;
  public bookingPrice: number;
  public isPurchased: boolean;
  public currency: string;

  constructor({ trainingId, trainingPeriodId, eventId, coinsPrice, ticketName, ticketTypeId, bookingPrice, isPurchased, promoCoinsPrice, promoEndTime, currency }: any) {
    this.eventId = eventId;
    this.trainingId = trainingId;
    this.trainingPeriodId = trainingPeriodId;
    this.coinsPrice = coinsPrice;
    this.promoCoinsPrice = promoCoinsPrice;
    this.promoEndTime = promoEndTime || null;
    this.ticketName = ticketName;
    this.ticketTypeId = ticketTypeId;
    this.isPurchased = isPurchased;
    this.selected = false;
    this.bookingPrice = bookingPrice || coinsPrice;
    this.currency = currency || 'Ȼ';
    extendObservable(this, {
      selected: this.selected
    });
  }

  @computed
  public get nameString(): string {
    return this.ticketName;
  }

  @computed
  public get effectiveCoinsPrice(): number {
    return this.promoCoinsPrice !== null && this.promoCoinsPrice !== undefined ? this.promoCoinsPrice : this.coinsPrice;
  }

  @computed
  public get type(): string {
    return "info";
  }

}

export class SellingItem {
  public trainingId: number;
  public trainingPeriodId: number;
  public sellingItemId: number;
  public name: string;
  public cost: number;
  public time: Time;
  public description: string;
  public selected: boolean;
  public bookingPrice: number;
  public venueId: number;
  public venueName: string;
  public venueStreet: string;
  public venueCity: string;
  public venuePriority: number;
  // New discount-related fields
  public discountedSessionCost: number;
  public discountName: string;
  public discountType: string;
  public discountValue: number;
  public discountRuleId: number;
  public isCheckedIn: boolean;
  public canCancel: boolean;

  // Level requirement field
  public levelSufficient: boolean;

  constructor({ trainingId, trainingPeriodId, sellingItemId, name, cost, time, description, venueId, venueName, venueStreet, venueCity, venuePriority, discountedSessionCost, discountName, discountType, discountValue, discountRuleId, checkedIn, canCancel, levelSufficient }: any) {
    this.trainingId = trainingId;
    this.trainingPeriodId = trainingPeriodId;
    this.sellingItemId = sellingItemId;
    this.name = name;
    this.cost = cost;
    this.time = time ? new Time(time) : undefined;
    this.selected = false;
    this.description = description;
    this.venueId = venueId;
    this.venueName = venueName;
    this.venueStreet = venueStreet;
    this.venueCity = venueCity;
    this.venuePriority = venuePriority;
    // Initialize discount fields
    this.discountedSessionCost = discountedSessionCost;
    this.discountName = discountName;
    this.discountType = discountType;
    this.discountValue = discountValue;
    this.discountRuleId = discountRuleId;
    this.isCheckedIn = checkedIn;
    this.canCancel = canCancel;

    // Initialize level requirement field
    this.levelSufficient = levelSufficient;
    
    extendObservable(this, {
      selected: this.selected
    });
  }

  @computed
  public get nameString(): string {
    return this.isPremium ? `👑 ${this.name} 👑` : this.name;
  }

  @computed
  public get title(): string {
    const timeString = this.time ? `${this.time.timeString} ` : '';
    return `${timeString}${this.nameString}`;
  }

  @computed
  public get isPremium(): boolean {
    return this.name.toLowerCase().includes("premium");
  }

  @computed
  public get type(): string {
    if (this.isPremium) {
      return "dark";
    }
    return "info";
  }
}

export class Time {
  public duration: number;
  public hour: number;
  public minute: number;
  public date: string;

  constructor({ duration, hour, minute, date }) {
    this.duration = duration;
    this.hour = hour;
    this.minute = minute;
    this.date = date;
  }

  public get timeString(): string {
    const start = padNumber(this.hour) + ':' + padNumber(this.minute);
    if (!this.duration) return start;
    const totalEndMinutes = this.hour * 60 + this.minute + this.duration;
    const endHour = Math.floor(totalEndMinutes / 60) % 24;
    const endMinute = totalEndMinutes % 60;
    return start + '\u2013' + padNumber(endHour) + ':' + padNumber(endMinute);
  }
}

export function costString(cost?: number, currency: string = 'Ȼ') {
  return cost !== null && cost !== undefined ? `${currency} ${cost}` : '';
}

export function dualCostString(originalCost?: number, promoCost?: number, currency: string = 'Ȼ', promoEndTime?: string | null): React.ReactElement | string {
  if (originalCost === null && promoCost === null) return '';
  if (originalCost === undefined && promoCost === undefined) return '';

  // If original cost is 0, always just show 0 (no promo needed - already free)
  if (originalCost === 0) {
    return costString(0, currency);
  }

  // If no promo price (null/undefined), just show original price
  if (promoCost === null || promoCost === undefined) {
    return costString(originalCost, currency);
  }

  // If promo price equals original price, no discount - just show original
  if (promoCost === originalCost) {
    return costString(originalCost, currency);
  }

  // Format promo end date if available
  let promoEndLabel: string | null = null;
  if (promoEndTime) {
    const endDate = new Date(promoEndTime);
    const month = endDate.toLocaleString('en-US', { month: 'short' });
    const day = endDate.getDate();
    promoEndLabel = `until ${month} ${day}`;
  }

  // Show discount: strikethrough original, highlighted promo price (even if promo is 0 - free!)
  return React.createElement(React.Fragment, {},
    React.createElement('span', {
      style: { textDecoration: 'line-through', marginRight: '8px' }
    }, costString(originalCost, currency)),
    React.createElement('span', {
      className: 'discounted-cost'
    }, costString(promoCost, currency)),
    promoEndLabel ? React.createElement('span', {
      style: { marginLeft: '6px', fontSize: '0.85em', color: '#28a745', fontWeight: 500 }
    }, promoEndLabel) : null
  );
}

// Student Progress interfaces
export interface LevelPromotion {
  assignmentId: number;
  levelId: number;
  styleId: number;
  styleName: string;
  levelNumber: number;
  levelName: string;
  levelColor: string;
  levelDescription: string;
  achievedAt: Date;
  minimumAttendance?: number;
  recommendedAttendance?: number;
}

export interface SyllabusTopic {
  id: number;
  styleId: number;
  topicName: string;
  topicDescription?: string;
  videoLink?: string;
  topicOrder: number;
  isLearned: boolean;
}

export interface LevelProgress {
  promotion: LevelPromotion;
  syllabusTopics: SyllabusTopic[];
  totalTopics: number;
  learnedTopics: number;
  progressPercentage: number;
}

export type { StreakMilestone };

export interface AttendanceStats {
  totalClassesAttended: number;
  currentStreak: number;
  longestStreak: number;
  attendanceByMonth: { [key: string]: number };
  attendanceByStyle: { [key: string]: number };
  milestones: StreakMilestone[];
  nextMilestoneWeeks: number | null;
  nextMilestoneName: string | null;
  attendedWeeks: { [key: string]: number };
  streakAtRisk: boolean;
  streakInfoMessage: string | null;
  streakResetWeeks: number | null;
}

export interface StudentProgress {
  levelProgressList: LevelProgress[];
  attendanceStats: AttendanceStats;
}
